import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Users, 
  TrendingUp, 
  Clock, 
  Calendar,
  BarChart3,
  Activity,
  Target
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

interface ChatbotStats {
  totalConversations: number;
  totalLeads: number;
  conversionRate: number;
  averageResponseTime: number;
  todayConversations: number;
  weeklyConversations: number;
  topQuestions: Array<{ question: string; count: number }>;
  hourlyActivity: Array<{ hour: number; count: number }>;
}

export const ChatbotStatistics: React.FC = () => {
  const [stats, setStats] = useState<ChatbotStats>({
    totalConversations: 0,
    totalLeads: 0,
    conversionRate: 0,
    averageResponseTime: 0,
    todayConversations: 0,
    weeklyConversations: 0,
    topQuestions: [],
    hourlyActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week'); // today, week, month

  useEffect(() => {
    loadStatistics();
  }, [dateRange]);

  const loadStatistics = async () => {
    try {
      // Pobierz konwersacje
      const { data: conversations } = await supabase
        .from('conversations')
        .select('*')
        .order('created_at', { ascending: false });

      // Pobierz leady z czatbota
      const { data: leads } = await supabase
        .from('leads')
        .select('*')
        .eq('source', 'chatbot');

      // Pobierz wiadomości dla analizy
      const { data: messages } = await supabase
        .from('messages')
        .select('*')
        .eq('role', 'user');

      if (conversations && leads && messages) {
        // Oblicz statystyki
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);

        const todayConversations = conversations.filter(c => 
          new Date(c.created_at) >= today
        ).length;

        const weeklyConversations = conversations.filter(c => 
          new Date(c.created_at) >= weekAgo
        ).length;

        const conversionRate = conversations.length > 0 
          ? (leads.length / conversations.length) * 100 
          : 0;

        // Analiza najczęstszych pytań
        const questionCounts: Record<string, number> = {};
        messages.forEach(msg => {
          const content = msg.content.toLowerCase();
          if (content.includes('cen') || content.includes('koszt')) {
            questionCounts['Pytania o cenę'] = (questionCounts['Pytania o cenę'] || 0) + 1;
          }
          if (content.includes('plexi')) {
            questionCounts['Pytania o plexi'] = (questionCounts['Pytania o plexi'] || 0) + 1;
          }
          if (content.includes('termin') || content.includes('czas')) {
            questionCounts['Pytania o termin'] = (questionCounts['Pytania o termin'] || 0) + 1;
          }
          if (content.includes('transport') || content.includes('dostaw')) {
            questionCounts['Pytania o dostawę'] = (questionCounts['Pytania o dostawę'] || 0) + 1;
          }
        });

        const topQuestions = Object.entries(questionCounts)
          .map(([question, count]) => ({ question, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        // Aktywność godzinowa
        const hourlyActivity = Array.from({ length: 24 }, (_, hour) => {
          const count = conversations.filter(c => {
            const convHour = new Date(c.created_at).getHours();
            return convHour === hour;
          }).length;
          return { hour, count };
        });

        setStats({
          totalConversations: conversations.length,
          totalLeads: leads.length,
          conversionRate,
          averageResponseTime: 2.5, // przykładowa wartość
          todayConversations,
          weeklyConversations,
          topQuestions,
          hourlyActivity
        });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Statystyki Czatbota</h2>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="today">Dzisiaj</option>
          <option value="week">Ostatni tydzień</option>
          <option value="month">Ostatni miesiąc</option>
        </select>
      </div>

      {/* Główne metryki */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rozmowy</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalConversations}</p>
              <p className="text-xs text-gray-500 mt-1">Dzisiaj: {stats.todayConversations}</p>
            </div>
            <MessageSquare className="h-8 w-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Leady</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalLeads}</p>
              <p className="text-xs text-gray-500 mt-1">Z czatbota</p>
            </div>
            <Users className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Konwersja</p>
              <p className="text-2xl font-bold text-gray-900">{stats.conversionRate.toFixed(1)}%</p>
              <p className="text-xs text-gray-500 mt-1">Lead/Rozmowa</p>
            </div>
            <Target className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Czas odpowiedzi</p>
              <p className="text-2xl font-bold text-gray-900">{stats.averageResponseTime}s</p>
              <p className="text-xs text-gray-500 mt-1">Średni</p>
            </div>
            <Clock className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Wykresy */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Najczęstsze pytania */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-orange-500" />
            Najczęstsze tematy
          </h3>
          <div className="space-y-3">
            {stats.topQuestions.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{item.question}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-orange-500 h-2 rounded-full"
                      style={{ width: `${(item.count / stats.topQuestions[0]?.count || 1) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-10 text-right">
                    {item.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Aktywność godzinowa */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-orange-500" />
            Aktywność w ciągu dnia
          </h3>
          <div className="flex items-end justify-between h-32">
            {stats.hourlyActivity.slice(8, 20).map((item) => (
              <div
                key={item.hour}
                className="flex-1 mx-0.5"
                title={`${item.hour}:00 - ${item.count} rozmów`}
              >
                <div
                  className="bg-orange-500 rounded-t hover:bg-orange-600 transition-colors cursor-pointer"
                  style={{ 
                    height: `${(item.count / Math.max(...stats.hourlyActivity.map(h => h.count), 1)) * 100}%`,
                    minHeight: item.count > 0 ? '4px' : '0'
                  }}
                />
                <p className="text-xs text-gray-500 text-center mt-1">
                  {item.hour}
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center mt-2">Godziny pracy (8:00 - 20:00)</p>
        </div>
      </div>

      {/* Ostatnie konwersacje */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-orange-500" />
          Ostatnia aktywność
        </h3>
        <div className="text-sm text-gray-600">
          <p>Ostatnia rozmowa: <span className="font-medium">5 minut temu</span></p>
          <p>Ostatni lead: <span className="font-medium">2 godziny temu</span></p>
          <p>Tygodniowy trend: <span className="font-medium text-green-600">+15%</span></p>
        </div>
      </div>
    </div>
  );
};
